import truncate from 'lodash.truncate'
import Head from 'next/head'
import { useRouter } from 'next/router'
import PropTypes from 'prop-types'
import { allPass, ascend, compose, find, isNil, map, pathEq, prop, reject, sort, uniqBy } from 'ramda'
import { useCallback, useEffect, useState } from 'react'

import { formatMoney, isBlank, isPresent } from 'utils'

import { useCart } from 'hooks'

import Button from 'components/Button'
import Header from 'components/Header'
import ProductColorOptions from 'components/ProductColorOptions'
import ProductDescription from 'components/ProductDescription'
import ProductOptions from 'components/ProductOptions'
import ProductPhotos from 'components/ProductPhotos'
import Spacer from 'components/Spacer'
import SubHeader from 'components/SubHeader'
import Text from 'components/Text'

import {
  Artisan,
  Details,
  Information,
  Options,
  Photos,
  PhotosAndInformation,
  Price,
  ProductTitle,
  Root,
  SizeGuide,
  SizeGuideAndDetails,
  SizeGuideAndDetailsHeader,
  SizeGuideAndDetailsWrapper,
  SizeGuideTable,
  Sku,
  Title,
} from './Product.style'

const sortBySortOrderProp = sort(ascend(prop('sortOrder')))
const skuOptionsForKey = (key) => compose(reject(isNil), sortBySortOrderProp, uniqBy(prop('name')), map(prop(key)))

const findSkuByOptions = (skus, options) => {
  const conditions = Object.keys(options).map((key) => {
    const value = options[key]

    if (!value) return null

    return pathEq([key, 'name'], value)
  })

  return find(allPass(conditions.filter(Boolean)))(skus)
}

const Product = ({ product, skuId: _skuId, artisanUrl }) => {
  const router = useRouter()

  const { linesAdd, lines } = useCart()

  const {
    artisan,
    title,
    price,
    sizesByColor,
    images: allImages,
    imagesByColor,
    zoomedPhotos,
    descriptionHtml,
    descriptionText,
    sizingHeaderHtml,
    sizingChartHtml,
    detailsHtml,
    defaultSku,
    skus,
    sku,
  } = product

  const colors = skuOptionsForKey('color')(skus)
  const sizes = skuOptionsForKey('size')(skus)

  const [selectedOptions, setSelectedOptions] = useState(null)
  const [selectedSku, setSelectedSku] = useState(null)
  const [images, setImages] = useState([])
  const [buttonText, setButtonText] = useState('Unavailable')
  const [buttonBusy, setButtonBusy] = useState(false)

  const handleOptionChange = (name, value) => {
    const newOptions = { ...selectedOptions, [name]: value }
    skuFromOptions(newOptions)
  }

  const handleAddToCart = async () => {
    setButtonBusy(true)
    await linesAdd({ merchandiseId: selectedSku.shopifyVariantId })
    setButtonBusy(false)
  }

  const updateUrl = useCallback(
    (sku) => {
      if (sku && router.query.variant !== sku.id) {
        const query = {
          handle: router.query.handle,
          variant: sku.id,
        }
        if (router.query.uid) query.uid = router.query.uid
        router.replace(
          {
            pathname: router.pathname,
            query,
          },
          undefined,
          { shallow: true }
        )
      }
    },
    [router]
  )

  const skuFromOptions = useCallback(
    (selectedOptions) => {
      const sku = findSkuByOptions(skus, selectedOptions)

      setSelectedOptions(selectedOptions)
      setSelectedSku(sku)

      setImages(imagesByColor?.[selectedOptions.color] || allImages || [])

      updateUrl(sku)
    },
    [imagesByColor, allImages, skus, updateUrl]
  )

  const metaTitle = truncate(title, { length: 70, separator: ' ' })
  const metaDescription = truncate(descriptionText, { length: 200, separator: ' ' })

  const metaTags = [
    {
      name: 'og:url',
      content: `https://thearmoury.com${router.asPath}`,
    },
    { name: 'og:type', content: 'product' },
    { name: 'og:title', content: metaTitle },
    { name: 'og:description', content: metaDescription },
    { name: 'og:price:amount', content: price },
    { name: 'og:price:currency', content: 'USD' },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:site', content: '@thearmouryhk' },
    { name: 'twitter:title', content: metaTitle },
    { name: 'twitter:description', content: metaDescription },
  ]

  if (images.length > 0) {
    const content = images[0].replace('.jpg', '_grande.jpg')

    metaTags.push({
      name: 'og:image',
      content,
    })

    metaTags.push({
      name: 'twitter:image',
      content,
    })
  }

  useEffect(() => {
    if (isBlank(selectedSku)) {
      setButtonText('Unavailable')
      return undefined
    }

    const inCart = lines.find(({ merchandise: { id } }) => selectedSku?.shopifyVariantId === id)

    let text = 'Add to cart'
    if (!selectedSku?.isAvailable) text = 'Sold out'
    if (inCart) text = 'In your cart'

    setButtonText(text)
  }, [selectedSku, lines])

  useEffect(() => {
    const newOptions = {
      color: defaultSku.color?.name,
      size: defaultSku.size?.name,
    }

    skuFromOptions(newOptions)
    setSelectedOptions(newOptions)
  }, [sku])

  return (
    <>
      <Head>
        <title>{[product.title, 'The Armoury'].join(' - ')}</title>
        {metaTags.map(({ name, content }) => (
          <meta key={name} name={name} content={content} />
        ))}
      </Head>
      <Root>
        <PhotosAndInformation>
          <Photos>
            <ProductPhotos photos={images} />
          </Photos>
          <Information>
            <Title>
              <Artisan>
                {isPresent(artisanUrl) ? (
                  <Button variant="inline" text={artisan} as="a" href={artisanUrl} />
                ) : (
                  <SubHeader>{artisan}</SubHeader>
                )}
              </Artisan>
              <ProductTitle>
                <Header size="lg" balance>
                  {title}
                </Header>
              </ProductTitle>
              <Price>
                <Text font="stint" size="lg">
                  {formatMoney(selectedSku?.price || price, { fractional: false })}
                </Text>
              </Price>
            </Title>
            <Options>
              {isPresent(colors) && (
                <ProductColorOptions
                  colors={colors}
                  photos={zoomedPhotos}
                  onChange={handleOptionChange}
                  value={selectedOptions?.color}
                />
              )}
              {isPresent(sizes) && (
                <ProductOptions
                  name="size"
                  options={sizes}
                  enabledOptions={sizesByColor ? sizesByColor[selectedOptions?.color] : sizes}
                  onChange={handleOptionChange}
                  value={selectedOptions?.size}
                  isDisabled={!selectedSku?.isAvailable}
                />
              )}
              <Button
                text={buttonBusy ? 'One moment…' : buttonText}
                disabled={!selectedSku?.isAvailable}
                variant="outlined"
                width="full"
                onClick={handleAddToCart}
              />
            </Options>
            <ProductDescription html={descriptionHtml} />
            <Sku>
              <Text size="xs">SKU: {selectedSku?.sku || 'Unavailable'}</Text>
            </Sku>
          </Information>
        </PhotosAndInformation>
        <SizeGuideAndDetailsWrapper>
          <SizeGuideAndDetails>
            <SizeGuide>
              <SizeGuideAndDetailsHeader>
                <Header size="sm">Size Guide</Header>
              </SizeGuideAndDetailsHeader>
              <Text html={sizingHeaderHtml} />
              {isPresent(sizingChartHtml) && (
                <>
                  <Spacer y={8} />
                  <SizeGuideTable>
                    <Text html={sizingChartHtml} />
                  </SizeGuideTable>
                </>
              )}
            </SizeGuide>
            <Details>
              <SizeGuideAndDetailsHeader>
                <Header size="sm">Product Details</Header>
              </SizeGuideAndDetailsHeader>
              <Text html={detailsHtml} />
            </Details>
          </SizeGuideAndDetails>
        </SizeGuideAndDetailsWrapper>
      </Root>
    </>
  )
}

Product.propTypes = {
  product: PropTypes.object.isRequired,
  skuId: PropTypes.string,
}

Product.defaultProps = {}

export default Product
